import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';

import { EmptyState } from 'src/ui/components';

import {
  AddressDisplay,
  ChoiceDisplay,
  DobCommentsDisplay,
  DobDisplay,
  LookupDisplay,
} from './DisplayComponents';
import {
  AddressEdit,
  ChoiceEdit,
  DateEdit,
  DobCommentsEdit,
  DobEdit,
  LookupEdit,
  TextEdit,
} from './EditComponents';

import { useAuth, useTextTypeFormat } from 'src/hooks';

import { restrictedQuestions } from '../../utils/personRepoConstants';
import { usePersonRepoStore } from '../../store/personRepoStore';

import {
  ResponseType,
  SectionsAndQuestions,
} from '../../types/QuestionsTabType';

function QuestionsTab({ reportData }: { reportData: any }): ReactElement {
  const { token, tokenType } = useAuth();

  const userMode = usePersonRepoStore(useCallback(state => state.userMode, []));

  const getPersonQuestionInformation = usePersonRepoStore(
    useCallback(state => state.updateQuestionDetails, []),
  );

  const selectedPerson = usePersonRepoStore(
    useCallback(state => state.selectedPerson, []),
  );

  //fetch api for person alerts
  useQuery(
    [
      'get-person-question-details',
      {
        id: selectedPerson?.Id,
        accountId: selectedPerson?.AccountId,
      },
    ],
    () => {
      getPersonQuestionInformation(
        selectedPerson?.AccountId,
        selectedPerson?.Id,
        selectedPerson?.EntityId,
        token,
        tokenType,
      );
    },
    { staleTime: Infinity },
  );

  const questionDet = usePersonRepoStore(
    useCallback(state => state.question, []),
  );

  const { data: textTypeFormat } = useTextTypeFormat();

  //setting the zustand variable with texttype array
  useEffect(() => {
    if (textTypeFormat) {
      usePersonRepoStore.getState().setTextTypeFormat(textTypeFormat);
    }
  }, [textTypeFormat]);

  function showSection(section: SectionsAndQuestions) {
    let val = 0;
    section?.Questions?.map((item: ResponseType) => {
      if (restrictedQuestions(item)) {
        val = val + 1;
      }
    });
    if (val > 0) return true;
    else return false;
  }

  return (
    <>
      <div className="h-full py-4 lg:py-6">
        {userMode === 'Edit' && reportData?.length > 0 ? (
          <div className="min-w-[450px] space-y-6 pb-px">
            {reportData.map(
              (section: any) =>
                section &&
                showSection(section) && (
                  <dl
                    className="mb-4 border dark:border-dark-medium lg:mb-6"
                    key={section?.Id}
                  >
                    <dt className="sticky -top-px border-y bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
                      {section?.Title}
                    </dt>
                    {section?.Questions.map(
                      (question: ResponseType) =>
                        restrictedQuestions(question) && (
                          <dd
                            className="flex gap-2 border-b dark:border-dark-medium"
                            key={question?.Id}
                          >
                            <h4 className="basis-1/3 px-3 py-2 text-sm font-medium">
                              {question?.FieldLabel}
                            </h4>

                            {question.Type === 'Text' ? (
                              <p className="basis-4/6 whitespace-pre-line px-3 py-2 text-sm">
                                {question.Value}
                              </p>
                            ) : question.Type === 'Choice' ? (
                              <p className="basis-4/6 px-3 py-2 text-sm">
                                <ChoiceDisplay question={question} />
                              </p>
                            ) : question.Type === 'Address' ? (
                              <p className="basis-4/6 px-3 py-2 text-sm">
                                <AddressDisplay question={question} />
                              </p>
                            ) : question.Type === 'Lookup' ? (
                              <p className="basis-4/6 px-3 py-2 text-sm">
                                <LookupDisplay question={question} />
                              </p>
                            ) : question.Type === 'Date' ? (
                              <p className="basis-4/6 px-3 py-2 text-sm">
                                {question.Value}
                              </p>
                            ) : question.Type ===
                              'Date Of Birth With Comment' ? (
                              <p className="basis-4/6 px-3 py-2 text-sm">
                                <DobCommentsDisplay question={question} />
                              </p>
                            ) : question.Type === 'Date Of Birth' ? (
                              <p className="basis-4/6 px-3 py-2 text-sm">
                                <DobDisplay question={question} />
                              </p>
                            ) : (
                              <p className="basis-4/6 px-3 py-2 text-sm"> </p>
                            )}
                          </dd>
                        ),
                    )}
                  </dl>
                ),
            )}
          </div>
        ) : (
          userMode === 'Edit' && (
            <div className="grid h-full w-full place-content-center">
              <EmptyState message="No information available" />
            </div>
          )
        )}

        {/*  */}
        {userMode === 'Update' && reportData?.length > 0 ? (
          <div className="min-h-96 min-w-[500px] space-y-6">
            {reportData &&
              reportData.map(
                (section: any) =>
                  section &&
                  showSection(section) && (
                    <dl
                      className="border dark:border-dark-medium"
                      key={section?.Id}
                    >
                      <dt className="sticky -top-px z-[1] border-y bg-gray-100 px-3 py-2 text-xl font-semibold dark:border-dark-medium dark:bg-neutral-900">
                        {section?.Title}
                      </dt>
                      {section?.Questions.map(
                        (question: ResponseType) =>
                          restrictedQuestions(question) && (
                            <>
                              {question.Type === 'Text' && (
                                <TextEdit question={question} />
                              )}
                              {question.Type === 'Address' && (
                                <AddressEdit question={question} />
                              )}
                              {question.Type === 'Choice' && (
                                <ChoiceEdit question={question} />
                              )}
                              {question.Type === 'Date' && (
                                <DateEdit question={question} />
                              )}
                              {question.Type ===
                                'Date Of Birth With Comment' && (
                                <DobCommentsEdit question={question} />
                              )}
                              {question.Type === 'Lookup' && (
                                <LookupEdit question={question} />
                              )}
                              {question.Type === 'Date Of Birth' && (
                                <DobEdit question={question} />
                              )}
                            </>
                          ),
                      )}
                    </dl>
                  ),
              )}
          </div>
        ) : null}
      </div>
    </>
  );
}

export { QuestionsTab };
